import { useDispatch, useSelector } from "react-redux";

import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import {
  getAdmAreaFilterAreaIds,
  hasSpatialAggregationChanged
} from "@/helpers/datasetAttributeHelper";
import { useAdmAreaFilterImpact } from "@/hooks/map/useAdmAreaFilterImpact";
import { useDataset } from "@/hooks/map/useDataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  setDatasetHasUpdatedAdmAreaMapping,
  updateDatasetAdmAreaLevel,
  updateDatasetCategories
} from "@/store/slice/datasetSlice";
import { DatasetDetailResponse, DatasetMetadata } from "@/types/dataset";

/**
 * Checks, on Generate Result, whether a dataset's Adm. Area Mapping changed
 * since it was loaded in a way that affects its saved ADM area filter.
 */
export const useAdmAreaMappingCheck = () => {
  const dispatch = useDispatch();
  const { datasets, filters } = useSelector(
    (state: RootState) => state.dataset
  );
  const { isAdmAreaForUserDataEnabled } = useAppFeatures();
  const checkAdmAreaFilterImpact = useAdmAreaFilterImpact();
  const { handleUpdateDatasetData, generateDatasetData } = useDataset();
  const { data: userProfileData } = useGetUserProfileAPI();

  const reloadDatasetData = async (datasetId: string) => {
    const dataset = datasets[datasetId];
    try {
      const { rawDataset, datasetMetadata } = await fetchDatasetStream({
        datasetId: datasetId.split("_")[0],
        filters: filters[datasetId] ?? [],
        timezone: userProfileData?.timezone,
        h3Configuration: dataset.h3Configuration,
        group_by: dataset.group_by,
        generateDatasetData
      });
      // The new mapping adds or removes ADM area columns, so the attribute
      // list must follow the new schema (same as editing the filter)
      const schema = datasetMetadata?.schema ?? [];
      if (
        schema.length &&
        JSON.stringify(schema) !== JSON.stringify(dataset.categories)
      ) {
        dispatch(updateDatasetCategories({ datasetId, categories: schema }));
      }
      if (rawDataset) {
        await handleUpdateDatasetData(
          datasetId,
          rawDataset,
          dataset.h3Configuration ?? null,
          dataset.group_by
        );
      }
    } catch {
      // The info still shows; the map keeps the data it already has
    }
  };

  /**
   * @returns `true` when Generate Result should stop so the user sees the
   * info. The new level is stored, so the next click goes ahead.
   */
  const checkAdmAreaMappingOnGenerate = async (
    datasetId: string,
    latestMetadata: DatasetMetadata | DatasetDetailResponse
  ): Promise<boolean> => {
    const dataset = datasets[datasetId];
    if (
      !isAdmAreaForUserDataEnabled ||
      latestMetadata.source !== "user" ||
      dataset.data.adm_area_level === latestMetadata.adm_area_level
    ) {
      return false;
    }
    dispatch(
      updateDatasetAdmAreaLevel({
        datasetId,
        admAreaLevel: latestMetadata.adm_area_level
      })
    );
    // A deeper mapping (e.g. City → District) keeps every level the filter
    // could use, so no area needs to be fetched
    const previousLevel = dataset.data.adm_area_level;
    const latestLevel = latestMetadata.adm_area_level;
    if (
      typeof previousLevel === "number" &&
      typeof latestLevel === "number" &&
      latestLevel > previousLevel
    ) {
      return false;
    }

    const admAreaFilterOptions = (filters[datasetId] ?? [])
      .filter((filter) => filter.attributeCode?.includes("adm_area_level"))
      .flatMap((filter) => filter.selectedOptions ?? []);
    const isImpacted = await checkAdmAreaFilterImpact(
      getAdmAreaFilterAreaIds(admAreaFilterOptions),
      latestMetadata.adm_area_level
    );
    if (!isImpacted) return false;

    dispatch(
      setDatasetHasUpdatedAdmAreaMapping({ datasetId, hasUpdated: true })
    );
    // A dataset whose spatial aggregation changed gets new data on Refresh
    const isWaitingForRefresh =
      dataset.properties.hasUpdatedSpatialAggregation ||
      hasSpatialAggregationChanged(
        dataset.data.spatial_aggregation_type,
        latestMetadata
      );
    if (!isWaitingForRefresh) await reloadDatasetData(datasetId);
    return true;
  };

  return { checkAdmAreaMappingOnGenerate };
};
